/**
 * Unified uploader component that combines FileUpload and ImageUploader functionality
 * Reduces code duplication and provides consistent upload experience
 */

import {
  AlertCircle,
  CheckCircle,
  Eye,
  File,
  FileText,
  Image,
  RotateCcw,
  Upload,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import uploadService, { UploadOptions } from '../../services/uploadService';

export interface UnifiedUploadItem {
  id: string;
  file: File;
  previewUrl?: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  referenceId?: string;
}

interface UnifiedUploaderProps {
  // File constraints
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in MB
  maxFiles?: number;

  // Upload configuration
  uploadEndpoint?: string;
  module?: string;
  projectId?: string;
  autoUpload?: boolean;

  // Image-specific options
  showImagePreviews?: boolean;
  enableImageCompression?: boolean;
  compressionQuality?: number;
  maxImageWidth?: number;
  maxImageHeight?: number;

  // UI configuration
  label?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  showProgress?: boolean;

  // Callbacks
  files: UnifiedUploadItem[];
  onChange: (files: UnifiedUploadItem[]) => void;
  onUploadComplete?: (files: UnifiedUploadItem[]) => void;
  onError?: (error: string) => void;
}

const UnifiedUploader: React.FC<UnifiedUploaderProps> = ({
  accept,
  multiple = false,
  maxSize = 10,
  maxFiles = 5,
  uploadEndpoint = '/api/files/upload',
  module = 'documents',
  projectId,
  autoUpload = false,
  showImagePreviews = true,
  enableImageCompression = true,
  compressionQuality = 0.8,
  maxImageWidth = 1920,
  maxImageHeight = 1080,
  label,
  hint,
  required = false,
  disabled = false,
  className = '',
  showProgress = true,
  files,
  onChange,
  onUploadComplete,
  onError,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { success, error: showError } = useToast();

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      files.forEach(fileItem => {
        if (fileItem.previewUrl) {
          URL.revokeObjectURL(fileItem.previewUrl);
        }
      });
    };
  }, []);

  const isImageFile = useCallback((file: File) => {
    return file.type.startsWith('image/');
  }, []);

  const createPreviewUrl = useCallback(
    (file: File) => {
      return isImageFile(file) ? URL.createObjectURL(file) : undefined;
    },
    [isImageFile]
  );

  const validateFile = useCallback(
    (file: File): { valid: boolean; error?: string } => {
      // Check file size
      if (file.size > maxSize * 1024 * 1024) {
        return { valid: false, error: `File size exceeds ${maxSize}MB limit` };
      }

      // Check file type if accept is specified
      if (accept) {
        const acceptedTypes = accept.split(',').map(type => type.trim());
        const isValidType = acceptedTypes.some(type => {
          if (type.includes('*')) {
            const baseType = type.split('/')[0];
            return file.type.startsWith(baseType);
          }
          return file.type === type;
        });

        if (!isValidType) {
          return { valid: false, error: `File type not supported. Accepted: ${accept}` };
        }
      }

      return { valid: true };
    },
    [accept, maxSize]
  );

  const handleFileSelect = useCallback(
    async (selectedFiles: FileList | null) => {
      if (!selectedFiles || disabled) return;

      const newFileItems: UnifiedUploadItem[] = [];
      const errors: string[] = [];

      Array.from(selectedFiles).forEach(file => {
        // Check file count
        if (files.length + newFileItems.length >= maxFiles) {
          errors.push(`Maximum ${maxFiles} files allowed`);
          return;
        }

        const validation = validateFile(file);
        if (!validation.valid) {
          errors.push(`${file.name}: ${validation.error}`);
          return;
        }

        newFileItems.push({
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          file,
          previewUrl: createPreviewUrl(file),
          progress: 0,
          status: 'pending',
        });
      });

      // Show validation errors
      if (errors.length > 0) {
        showError('File validation failed', errors.join(', '));
        onError?.(errors.join(', '));
        return;
      }

      const updatedFiles = multiple ? [...files, ...newFileItems] : newFileItems.slice(0, 1);
      onChange(updatedFiles);

      // Auto-upload if enabled
      if (autoUpload && newFileItems.length > 0) {
        await uploadFiles(newFileItems);
      }
    },
    [
      files,
      maxFiles,
      validateFile,
      multiple,
      onChange,
      autoUpload,
      disabled,
      showError,
      onError,
      createPreviewUrl,
    ]
  );

  const uploadFiles = useCallback(
    async (filesToUpload: UnifiedUploadItem[]) => {
      if (isUploading) return;

      setIsUploading(true);

      try {
        const uploadOptions: UploadOptions = {
          endpoint: uploadEndpoint,
          module,
          projectId,
          compression: {
            enabled: enableImageCompression,
            quality: compressionQuality,
            maxWidth: maxImageWidth,
            maxHeight: maxImageHeight,
          },
        };

        // Upload files in parallel
        const uploadPromises = filesToUpload.map(async fileItem => {
          // Update progress callback
          const onProgress = (progress: number) => {
            onChange(prevFiles =>
              prevFiles.map(f =>
                f.id === fileItem.id ? { ...f, progress, status: 'uploading' as const } : f
              )
            );
          };

          const result = await uploadService.uploadFile(fileItem.file, {
            ...uploadOptions,
            onProgress,
          });

          // Update file status
          onChange(prevFiles =>
            prevFiles.map(f =>
              f.id === fileItem.id
                ? {
                    ...f,
                    status: result.success ? ('completed' as const) : ('error' as const),
                    progress: result.success ? 100 : 0,
                    error: result.error,
                    referenceId: result.referenceId,
                  }
                : f
            )
          );

          return result;
        });

        const results = await Promise.all(uploadPromises);
        const successCount = results.filter(r => r.success).length;
        const failCount = results.length - successCount;

        if (successCount > 0) {
          success(
            `Successfully uploaded ${successCount} file(s)${failCount > 0 ? `, ${failCount} failed` : ''}`
          );
        }

        if (failCount === 0) {
          onUploadComplete?.(files);
        } else if (failCount === results.length) {
          const errorMsg = 'All uploads failed';
          showError(errorMsg);
          onError?.(errorMsg);
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Upload failed';
        showError('Upload failed', errorMsg);
        onError?.(errorMsg);
      } finally {
        setIsUploading(false);
      }
    },
    [
      isUploading,
      uploadEndpoint,
      module,
      projectId,
      enableImageCompression,
      compressionQuality,
      maxImageWidth,
      maxImageHeight,
      onChange,
      success,
      showError,
      onError,
      onUploadComplete,
      files,
    ]
  );

  const removeFile = useCallback(
    (fileId: string) => {
      const fileItem = files.find(f => f.id === fileId);
      if (fileItem?.previewUrl) {
        URL.revokeObjectURL(fileItem.previewUrl);
      }

      const newFiles = files.filter(f => f.id !== fileId);
      onChange(newFiles);
    },
    [files, onChange]
  );

  const retryUpload = useCallback(
    async (fileId: string) => {
      const fileItem = files.find(f => f.id === fileId);
      if (!fileItem) return;

      await uploadFiles([fileItem]);
    },
    [files, uploadFiles]
  );

  const getFileIcon = useCallback((file: File) => {
    if (file.type.startsWith('image/')) {
      return <Image className="h-4 w-4" />;
    } else if (file.type.includes('pdf') || file.type.includes('document')) {
      return <FileText className="h-4 w-4" />;
    }
    return <File className="h-4 w-4" />;
  }, []);

  const getStatusIcon = useCallback((fileItem: UnifiedUploadItem) => {
    switch (fileItem.status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'uploading':
        return (
          <div className="h-4 w-4 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
        );
      default:
        return null;
    }
  }, []);

  const formatFileSize = useCallback((bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // Event handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!disabled) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Determine if we should show image grid or file list
  const showImageGrid = showImagePreviews && files.some(f => isImageFile(f.file));

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragOver ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-gray-400'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={e => handleFileSelect(e.target.files)}
          className="hidden"
          disabled={disabled}
        />

        <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <p className="text-sm text-gray-600 mb-2">
          <span className="font-medium text-red-500">Click to upload</span> or drag and drop
        </p>
        <p className="text-xs text-gray-500">
          {accept ? `Accepted formats: ${accept}` : 'All file types accepted'}
          {maxSize && ` • Max size: ${maxSize}MB`}
          {multiple && maxFiles && ` • Max files: ${maxFiles}`}
        </p>
      </div>

      {/* File List or Image Grid */}
      {files.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-gray-700">
              Files ({files.length}/{maxFiles})
            </p>
            {!autoUpload && files.some(f => f.status === 'pending') && (
              <button
                onClick={() => uploadFiles(files.filter(f => f.status === 'pending'))}
                disabled={isUploading}
                className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 disabled:opacity-50"
              >
                {isUploading ? 'Uploading...' : 'Upload All'}
              </button>
            )}
          </div>

          {showImageGrid ? (
            /* Image Grid */
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {files.map(fileItem => (
                <div key={fileItem.id} className="relative group">
                  <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border">
                    {fileItem.previewUrl ? (
                      <img
                        src={fileItem.previewUrl}
                        alt={fileItem.file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {getFileIcon(fileItem.file)}
                      </div>
                    )}

                    {/* Status overlays */}
                    {fileItem.status === 'uploading' && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="text-white text-center">
                          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                          <p className="text-xs">Uploading...</p>
                          {showProgress && (
                            <p className="text-xs">{Math.round(fileItem.progress)}%</p>
                          )}
                        </div>
                      </div>
                    )}

                    {fileItem.status === 'error' && (
                      <div className="absolute inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center">
                        <div className="text-white text-center">
                          <AlertCircle className="w-6 h-6 mx-auto mb-1" />
                          <p className="text-xs">Upload failed</p>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              retryUpload(fileItem.id);
                            }}
                            className="mt-2 px-2 py-1 bg-white text-red-600 text-xs rounded hover:bg-gray-100"
                          >
                            <RotateCcw className="w-3 h-3 inline mr-1" />
                            Retry
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {fileItem.previewUrl && (
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          window.open(fileItem.previewUrl, '_blank');
                        }}
                        className="p-1 bg-white rounded-full shadow-md hover:bg-gray-50"
                      >
                        <Eye className="w-4 h-4 text-gray-600" />
                      </button>
                    )}
                    {!disabled && fileItem.status !== 'uploading' && (
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          removeFile(fileItem.id);
                        }}
                        className="p-1 bg-white rounded-full shadow-md hover:bg-gray-50"
                      >
                        <X className="w-4 h-4 text-red-600" />
                      </button>
                    )}
                  </div>

                  {/* File info */}
                  <div className="mt-2">
                    <p className="text-xs text-gray-600 truncate" title={fileItem.file.name}>
                      {fileItem.file.name}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{formatFileSize(fileItem.file.size)}</span>
                      {fileItem.status === 'completed' && (
                        <span className="text-green-600 font-medium">✓ Uploaded</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* File List */
            <div className="space-y-2">
              {files.map(fileItem => (
                <div
                  key={fileItem.id}
                  className={`p-3 rounded-lg border ${
                    fileItem.status === 'error'
                      ? 'bg-red-50 border-red-200'
                      : fileItem.status === 'completed'
                        ? 'bg-green-50 border-green-200'
                        : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      {getFileIcon(fileItem.file)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {fileItem.file.name}
                        </p>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          <span>{formatFileSize(fileItem.file.size)}</span>
                          {getStatusIcon(fileItem)}
                          {fileItem.status === 'uploading' && showProgress && (
                            <span>{Math.round(fileItem.progress)}%</span>
                          )}
                        </div>
                        {fileItem.error && (
                          <p className="text-xs text-red-600 mt-1">{fileItem.error}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-1">
                      {fileItem.status === 'error' && (
                        <button
                          type="button"
                          onClick={() => retryUpload(fileItem.id)}
                          className="p-1 text-orange-500 hover:text-orange-700 transition-colors"
                          title="Retry upload"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      )}
                      {!disabled && fileItem.status !== 'uploading' && (
                        <button
                          type="button"
                          onClick={() => removeFile(fileItem.id)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove file"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {showProgress && fileItem.status === 'uploading' && (
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-brand-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${fileItem.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {hint && <p className="mt-1 text-sm text-gray-500">{hint}</p>}
    </div>
  );
};

export default UnifiedUploader;
